/**
 * Global theme script (classic script, loaded with defer). Components register their
 * own custom elements; this file only holds cross-cutting helpers and shared elements.
 */
(function () {
  // Page scroll is locked while a `scroll-lock` dialog is open by CSS alone: html:has(dialog[scroll-lock][open])
  // in assets/critical.css (tests/e2e/dialog-scroll-lock.mjs).
  window.Miscela = window.Miscela || {};

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /**
   * Scheduled messages and banners (snippets/schedule.liquid). The page may come from cache (Liquid's `now` is the
   * render time), so each item's show dates are checked here on the visitor's clock: outside them it is hidden, and a
   * bar, carousel or tile row with nothing left is hidden too. The next start or end within 24 hours is waited for, so
   * a page left open switches on time. In the theme editor every item shows.
   */
  const DAY = 24 * 60 * 60 * 1000;
  let scheduleTimer;
  function applySchedule() {
    if (window.Shopify && window.Shopify.designMode) return;
    const now = Date.now();
    let next = Infinity;
    document.querySelectorAll('[data-schedule]').forEach((item) => {
      const from = Date.parse(item.dataset.showFrom || '');
      const until = Date.parse(item.dataset.showUntil || '');
      // A missing date (NaN) never hides the item.
      item.hidden = from > now || until < now;
      if (from > now) next = Math.min(next, from);
      if (until >= now) next = Math.min(next, until + 1000);
    });
    document.querySelectorAll('[data-schedule-group]').forEach((group) => {
      group.hidden = ![...group.querySelectorAll('[data-schedule-item]')].some((item) => !item.hidden);
    });
    document.dispatchEvent(new CustomEvent('miscela:schedule'));
    window.clearTimeout(scheduleTimer);
    if (next - now <= DAY) scheduleTimer = window.setTimeout(applySchedule, next - now);
  }
  applySchedule();

  /**
   * Horizontal carousel on top of native scroll + scroll-snap.
   *
   * Markup (controls come from snippets/carousel-controls.liquid):
   *   <scroll-carousel data-autoplay="6000" data-loop>
   *     <div data-carousel-track> …slides… </div>
   *     <button data-carousel-prev> <button data-carousel-next> <div data-carousel-dots>
   *   </scroll-carousel>
   *
   * A "page" is one track width, so the same element works for one slide per view (hero)
   * and for several (product showcases).
   */
  class ScrollCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-carousel-track]');
      if (!this.track) return;
      this.prevButton = this.querySelector('[data-carousel-prev]');
      this.nextButton = this.querySelector('[data-carousel-next]');
      this.dotsContainer = this.querySelector('[data-carousel-dots]');
      this.autoplayDelay = Number(this.dataset.autoplay || 0);
      this.loop = this.hasAttribute('data-loop');
      this.page = 0;

      this.prevButton?.addEventListener('click', () => this.step(-1));
      this.nextButton?.addEventListener('click', () => this.step(1));
      this.track.addEventListener('scroll', () => this.scheduleUpdate(), { passive: true });

      this.resizeObserver = new ResizeObserver(() => this.build());
      this.resizeObserver.observe(this.track);
      // A scheduled slide hidden or shown changes the pages without resizing the track.
      this.onSchedule = () => this.build();
      document.addEventListener('miscela:schedule', this.onSchedule);

      if (this.autoplayDelay > 0) {
        this.paused = false;
        const pause = () => (this.paused = true);
        const resume = () => (this.paused = false);
        this.addEventListener('pointerenter', pause);
        this.addEventListener('pointerleave', resume);
        this.addEventListener('focusin', pause);
        this.addEventListener('focusout', resume);
        this.timer = window.setInterval(() => {
          if (!this.paused && !document.hidden && !reducedMotion.matches) this.step(1);
        }, this.autoplayDelay);
      }
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      document.removeEventListener('miscela:schedule', this.onSchedule);
      window.clearInterval(this.timer);
    }

    get maxScroll() {
      return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    }

    get pageCount() {
      if (this.maxScroll <= 1) return 1;
      return Math.ceil(this.maxScroll / this.track.clientWidth - 0.05) + 1;
    }

    build() {
      const count = this.pageCount;
      this.toggleAttribute('data-static', count <= 1);
      if (this.dotsContainer && this.dotsContainer.childElementCount !== count) {
        this.dotsContainer.replaceChildren(
          ...Array.from({ length: count }, (_, index) => {
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'carousel__dot';
            dot.setAttribute('aria-label', `Ir para ${index + 1} de ${count}`);
            dot.addEventListener('click', () => this.goTo(index));
            return dot;
          })
        );
      }
      this.update();
    }

    scheduleUpdate() {
      if (this.frame) return;
      this.frame = window.requestAnimationFrame(() => {
        this.frame = null;
        this.update();
      });
    }

    update() {
      const { scrollLeft, clientWidth } = this.track;
      const max = this.maxScroll;
      const count = this.pageCount;
      this.page = scrollLeft >= max - 2 ? count - 1 : Math.min(count - 1, Math.round(scrollLeft / clientWidth));
      this.dotsContainer?.querySelectorAll('.carousel__dot').forEach((dot, index) => {
        dot.setAttribute('aria-current', String(index === this.page));
      });
      if (this.prevButton) this.prevButton.disabled = !this.loop && scrollLeft <= 2;
      if (this.nextButton) this.nextButton.disabled = !this.loop && scrollLeft >= max - 2;
    }

    goTo(index) {
      const left = Math.min(this.maxScroll, Math.max(0, index) * this.track.clientWidth);
      this.track.scrollTo({ left, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    }

    step(direction) {
      const count = this.pageCount;
      let target = this.page + direction;
      if (target >= count) target = this.loop ? 0 : count - 1;
      if (target < 0) target = this.loop ? count - 1 : 0;
      this.goTo(target);
    }
  }

  if (!customElements.get('scroll-carousel')) customElements.define('scroll-carousel', ScrollCarousel);
})();
